export type Gender = 'female' | 'male';
export type ActivityLevelKey = 'minimal' | 'light' | 'moderate' | 'active' | 'very_active';
export type GoalKey = 'lose' | 'maintain' | 'gain';

export type DietKey =
    | 'high_protein_keto'
    | 'classic_keto'
    | 'mediterranean'
    | 'high_protein_low_carb'
    | 'zone'
    | 'modern_paleo';

export type DietProfile = {
    proteinPct: number; // 0..1
    fatPct: number; // 0..1
    carbsPct: number; // 0..1 (total carbs)
    sugarPct: number; // 0..1 (subset of carbs)
};

export type RecommendedResult = {
    calories: number;
    protein: number; // g/day
    fats: number; // g/day
    carbsNoSugars: number; // g/day
    sugars: number; // g/day
};

export const ACTIVITY_FACTORS: Record<ActivityLevelKey, number> = {
    minimal: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9,
};

export const GOAL_ADJUSTMENT: Record<GoalKey, number> = {
    lose: -0.2, // -20% от TDEE
    maintain: 0,
    gain: 0.15, // +15% к TDEE
};

export const DIETS: Record<DietKey, DietProfile> = {
    high_protein_keto: {
        proteinPct: 0.32,
        fatPct: 0.63,
        carbsPct: 0.05,
        sugarPct: 0.02,
    },
    classic_keto: {
        proteinPct: 0.22,
        fatPct: 0.73,
        carbsPct: 0.05,
        sugarPct: 0.02,
    },
    mediterranean: {
        proteinPct: 0.22,
        fatPct: 0.38,
        carbsPct: 0.4,
        sugarPct: 0.08,
    },
    high_protein_low_carb: {
        proteinPct: 0.38,
        fatPct: 0.33,
        carbsPct: 0.29,
        sugarPct: 0.05,
    },
    zone: {
        proteinPct: 0.3,
        fatPct: 0.3,
        carbsPct: 0.4,
        sugarPct: 0.06,
    },
    modern_paleo: {
        proteinPct: 0.28,
        fatPct: 0.37,
        carbsPct: 0.35,
        sugarPct: 0.05,
    },
};

// Mifflin–St Jeor
export const calculateBMR = (params: {
    gender: Gender;
    weightKg: number;
    heightCm: number;
    ageYears: number;
}): number => {
    const { gender, weightKg, heightCm, ageYears } = params;
    const base = 10 * weightKg + 6.25 * heightCm - 5 * ageYears;
    return gender === 'male' ? base + 5 : base - 161;
};

export const getActivityFactor = (key: ActivityLevelKey): number => {
    return ACTIVITY_FACTORS[key];
};

export const adjustCaloriesForGoal = (tdee: number, goal: GoalKey): number => {
    const adj = GOAL_ADJUSTMENT[goal];
    return Math.max(0, tdee * (1 + adj));
};

export const getDietProfile = (key: DietKey): DietProfile => {
    return DIETS[key];
};

export const roundTo = (val: number, digits = 0): number => {
    const p = Math.pow(10, digits);
    return Math.round(val * p) / p;
};

export const calculateMacros = (totalCalories: number, diet: DietProfile): RecommendedResult => {
    const proteinG = (totalCalories * diet.proteinPct) / 4;
    const fatG = (totalCalories * diet.fatPct) / 9;
    const carbsTotalG = (totalCalories * diet.carbsPct) / 4;
    const sugarG = (totalCalories * diet.sugarPct) / 4;
    const carbsNoSugarsG = Math.max(0, carbsTotalG - sugarG);

    return {
        calories: roundTo(totalCalories),
        protein: roundTo(proteinG),
        fats: roundTo(fatG),
        carbsNoSugars: roundTo(carbsNoSugarsG),
        sugars: roundTo(sugarG),
    };
};
